import { Role } from '@/api/CamundaController';
import { currencyCodes, DocumentType, goldCodes, RolesEnum } from '@/constatns';
import { DepartmentModel, RolesData } from '@/interface';

/** Любая модель, у которой есть код валюты: StartBalance, ExoticModel, остатки касс и т.д. */
export interface HasCurrencyCode {
    currencyCode?: string | null;
}

export const hasAnyRole = (roles: RolesData | undefined, flags: (keyof RolesData)[]) => flags.some((f) => !!roles?.[f]);

export const sortCurrencyFunc = (a: HasCurrencyCode, b: HasCurrencyCode) => {
    const xInB = currencyCodes.some(c => c === a.currencyCode);
    const yInB = currencyCodes.some(c => c === b.currencyCode);
    if (xInB && yInB) {
        return currencyCodes.findIndex(item => item === a.currencyCode) -
            currencyCodes.findIndex(item => item === b.currencyCode);
    }
    if (xInB) return -1;
    if (yInB) return 1;

    const xGold = goldCodes.some(c => c === a.currencyCode);
    const yGold = goldCodes.some(c => c === b.currencyCode);
    if (xGold && yGold) {
        return goldCodes.findIndex(item => item === a.currencyCode) -
            goldCodes.findIndex(item => item === b.currencyCode);
    }
    if (xGold) return -1;
    if (yGold) return 1;

    return 0;
};

const NATIONAL_BANK_DEPARTMENT_ID = 4;

/**
 * Пространство, для которого отделение-канал хранит курсы. Такие отделения
 * заведены по одному на регион («MOBILE_ALMATY», «WEBSITE_ALMATY»): касс и денег
 * у них нет, курс из них берут мобилка и ecash.kz — один на все точки региона.
 */
export enum RateChannel {
    Mobile = "MOBILE",
    Website = "WEBSITE",
}

/** Канал отделения по префиксу name; у обычной точки — null */
export const getRateChannel = (dep?: DepartmentModel): RateChannel | null => {
    const name = (dep?.name || "").trim().toUpperCase();

    if (name.startsWith(`${RateChannel.Mobile}_`)) return RateChannel.Mobile;
    if (name.startsWith(`${RateChannel.Website}_`)) return RateChannel.Website;

    return null;
};

export const isRateChannelDepartment = (dep?: DepartmentModel) => getRateChannel(dep) !== null;

export const hasDepartmentScope = (rolesData?: RolesData) => !!(
    rolesData?.mainCashierCity
    || rolesData?.managerCity
    || rolesData?.isNationalBank
    || rolesData?.franchiseTenantId
);

export const filterDepartmentsByRole = (
    departments?: DepartmentModel[],
    rolesData?: RolesData,
): DepartmentModel[] => {
    const deps = departments || [];

    if (rolesData?.mainCashierCity) {
        return deps.filter(dep => dep?.city === rolesData.mainCashierCity);
    }
    if (rolesData?.managerCity) {
        return deps.filter(dep => dep?.city === rolesData.managerCity);
    }
    if (rolesData?.isNationalBank) {
        return deps.filter(dep => dep?.id === NATIONAL_BANK_DEPARTMENT_ID);
    }
    if (rolesData?.franchiseTenantId) {
        return deps.filter(dep => dep?.tenantId === rolesData.franchiseTenantId);
    }

    return deps;
};

const isFranchiseDepartment = (dep?: DepartmentModel) => !!(dep?.isFranchise || dep?.tenantId);

/** Акцепты ограничены по отделениям у франшизы и у директора сети (без франшизы и без админских прав) */
export const hasAcceptDepartmentScope = (rolesData?: RolesData) => !!(
    rolesData?.franchiseTenantId
    || (rolesData?.isDirector && !rolesData?.isAdmin)
);

/**
 * Отделения, заявки и историю акцептов которых пользователь видит и может
 * акцептовать: франшиза - только точки своего тенанта, директор сети - только
 * собственные точки (не франшизные), остальные роли - все. Роль AcceptTenant_<id>
 * добавляет к ограниченной области точки указанного тенанта, но только там, где
 * передан withAcceptTenants: в возвратах, добавлении и удалении валюты. В бронях
 * и индивах она не действует.
 */
export const filterAcceptDepartments = (
    departments?: DepartmentModel[],
    rolesData?: RolesData,
    { withAcceptTenants = false }: { withAcceptTenants?: boolean } = {},
): DepartmentModel[] => {
    // Отделения-каналы — это только курсы, акцептовать по ним нечего
    const deps = (departments || []).filter(dep => !isRateChannelDepartment(dep));
    const isExtraTenant = (dep?: DepartmentModel) => withAcceptTenants && !!dep?.tenantId && !!rolesData?.acceptTenantIds?.includes(dep.tenantId);

    if (rolesData?.franchiseTenantId) {
        return deps.filter(dep => dep?.tenantId === rolesData.franchiseTenantId || isExtraTenant(dep));
    }
    if (rolesData?.isDirector && !rolesData?.isAdmin && !rolesData?.isTreasurer && !rolesData?.isTreasurerViewer) {
        return deps.filter(dep => !isFranchiseDepartment(dep) || isExtraTenant(dep));
    }

    return deps;
};

export const getRateDivisor = (currencyCode: string): number => {
    currencyCode === "VND" ? 1000 : ((currencyCode === "UZS" || currencyCode) === "KRW" ? 100 : 1);
    switch (currencyCode) {
        case "VND":
            return 1000;
        case "UZS":
            return 100;
        case "KRW":
            return 100;
        case "AMD":
            return 10;
        case "HUF":
            return 10;
        case "IDR":
            return 1000;
        case "IRR":
            return 10000;
        case "MNT":
            return 100;
        default:
            return 1;
    }
};

export const getID2Login = (kassaId?: string | number) => {
    let id2Login = "MAKHMUT";
    switch (kassaId) {
        case 4:
            id2Login = "FORUM";
            break;
        case 3:
            id2Login = "ZB1";
            break;
        case 6:
            id2Login = "GP1";
            break;
        case 5:
            id2Login = "APORT1";
            break;
        case 7:
            id2Login = "MEGAPARK1";
            break;
        case 8:
            id2Login = "SARYARKA1";
            break;
        case 9:
            id2Login = "APORT2_1";
            break;
        case 10:
            id2Login = "GP2";
            break;
        case 11:
            id2Login = "MEGAPARK_2";
            break;
        case 12:
            id2Login = "MAKHMUT";
            break;
        case 13:
            id2Login = "KHANSHATYR_1";
            break;
        case 14:
            id2Login = "KHANSHATYR_2";
            break;
        case 15:
            id2Login = "AEROPORT_1";
            break;
        case 16:
            id2Login = "AEROPORT_2";
            break;
        case 17:
            id2Login = "MEGASILKWAY_1";
            break;
        case 18:
            id2Login = "MEGASILKWAY_2";
            break;
        case 19:
            id2Login = "EURASIA1";
            break;
        case 20:
            id2Login = "ARUZHAN1";
            break;
        case 21:
            id2Login = "ABUDABI1";
            break;
        case 22:
            id2Login = "ABUDABI2";
            break;
        case 23:
            id2Login = "AKTOBE1";
            break;
        case 24:
            id2Login = "ALMATY_MALL_1";
            break;
        case 25:
            id2Login = "ALMATY_MALL_2";
            break;
        case 26:
            id2Login = "RITC_PALACE_1";
            break;
        case 27:
            id2Login = "SPUTNIK_1";
            break;
        case 28:
            id2Login = "AEROPORT1";
            break;
        case 29:
            id2Login = "AEROPORT2";
            break;
    }

    return id2Login;
};

// Российский паспорт: 9 цифр в формате "XX XXXXXXX" (10 символов), пробел проставляется автоматически
export function formatRuPassportInput(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, 9);

    return digits.length > 2 ? `${digits.slice(0, 2)} ${digits.slice(2)}` : digits;
}

// Клиенты, созданные до введения формата, хранят номер без пробела - приводим к "XX XXXXXXX" при подстановке в форму.
// Пробел - формат только российского паспорта: у казахстанского удостоверения и вида на жительство номер всегда
// 9 цифр подряд, даже если гражданство RU (россиянин с ВНЖ), поэтому такие документы не форматируем.
export function normalizeDocNumber(docNumber: string | undefined, citizenCode: string | undefined, docType?: string) {
    if (docType === DocumentType.ID || docType === DocumentType.RESIDENCE_PERMIT) {
        return (docNumber || "").replace(/\D/g, "").slice(0, 9);
    }

    return citizenCode === "RU" ? formatRuPassportInput(docNumber || "") : docNumber;
}

// Пробела может не быть у номеров, сохранённых до введения формата
export function isCorrectRuPassport(docNumber: string) {
    return /^\d{2} \d{7}$/.test(docNumber.trim()) || /^\d{9}$/.test(docNumber.trim());
}

export function formatPassportInput(value: string, citizenCode: string) {
    if (citizenCode === "RU") {
        value = formatRuPassportInput(value);
    } else if (citizenCode === "TJ") {
        value = value.replace(/\D/g, "").slice(0, 9);
    } else if (citizenCode === "IL") {
        value = value.replace(/\D/g, "").slice(0, 8);
    } else if (citizenCode === "SA") {
        value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
    } else {
        value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9);
    }

    return value;
}

export function checkIsCorrectDocumentNumber(isResident: boolean, docType: DocumentType | "", docNumber: string, citizenCode: string) {
    console.log(isResident,
        docType,
        docNumber,
        citizenCode,);
    if (isResident) {
        if (docType === "Удостоверение личности" || docType === "Вид на жительство") {
            return docNumber.length === 9;
        } else if ((docType === "Паспорт" || docType === "Удостоверение лица без гражданства")) {
            return docNumber.length === 9 && /^[A-Z]/.test(docNumber) && !(/^\d+$/.test(docNumber));
        }
    } else {
        return !(
            (citizenCode === "RU" && !isCorrectRuPassport(docNumber)) ||
            (citizenCode === "TJ" && docNumber.length !== 9) ||
            (citizenCode === "IL" && docNumber.length !== 8) ||
            (citizenCode === "SA" && !(docNumber.length === 7 && /^[A-Z][A-Z0-9]{0,5}[0-9]$/.test(docNumber))) ||
            (citizenCode !== "TJ" && citizenCode !== "SA" && citizenCode !== "RU" && citizenCode !== "IL" && !(docNumber.length === 9 || docNumber.length === 8))
        );
    }
}

export const checkPhone = (phone: string) => {
    if (
        phone.slice(1, 4) === "700" ||
        phone.slice(1, 4) === "701" ||
        phone.slice(1, 4) === "702" ||
        phone.slice(1, 4) === "703" ||
        phone.slice(1, 4) === "704" ||
        phone.slice(1, 4) === "705" ||
        phone.slice(1, 4) === "706" ||
        phone.slice(1, 4) === "707" ||
        phone.slice(1, 4) === "708" ||
        phone.slice(1, 4) === "709" ||
        phone.slice(1, 4) === "747" ||
        phone.slice(1, 4) === "750" ||
        phone.slice(1, 4) === "751" ||
        phone.slice(1, 4) === "760" ||
        phone.slice(1, 4) === "761" ||
        phone.slice(1, 4) === "762" ||
        phone.slice(1, 4) === "763" ||
        phone.slice(1, 4) === "764" ||
        phone.slice(1, 4) === "771" ||
        phone.slice(1, 4) === "775" ||
        phone.slice(1, 4) === "776" ||
        phone.slice(1, 4) === "777" ||
        phone.slice(1, 4) === "778" ||
        phone.slice(1, 4) === "700" ||
        phone.slice(1, 4) === "700" ||
        phone.slice(1, 4) === "700" ||
        phone.slice(1, 4) === "700" ||
        phone.slice(1, 4) === "700"
    ) {
        return true;
    }
    return false;
};

export const formatNumberRu = (price: number | string) => price?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
export const formatNumberRu2 = (num: number) => (+num)?.toFixed(3)?.replace(/(?:\.|,)?0+$/, "")?.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

const kkMonths = [
    "қаңтар",
    "ақпан",
    "наурыз",
    "сәуір",
    "мамыр",
    "маусым",
    "шілде",
    "тамыз",
    "қыркүйек",
    "қазан",
    "қараша",
    "желтоқсан",
];

export function formatDateKZ(date: Date) {
    return `${date.getDate()} ${kkMonths[date.getMonth()]} ${date.getFullYear()} ж.`;
}

// Бэк отдаёт UTC-моменты без "Z" и обрезает хвостовые нули микросекунд
// ("...50.010680" приходит как "...50.01068"), поэтому смотрим на дробную часть, а не на длину строки.
// Без "Z" браузер прочитал бы такую строку как местное время.
// ОФД присылает местное время кассы с миллисекундами ("...10:25:04.000") — ему "Z" дописывать нельзя,
// поэтому UTC считаем только строки с 4–6 цифрами после точки.
export const parseBackendDate = (isoString: string) =>
    new Date(isoString + (/T\d{2}:\d{2}:\d{2}\.\d{4,6}$/.test(isoString) ? "Z" : ""));

export const formatDate = (isoString?: string, month: "2-digit" | "long" = "2-digit", day: "2-digit" | "numeric" = "2-digit", timeZone: "Asia/Vladivostok" | "Asia/Yekaterinburg" | "UTC" = "Asia/Yekaterinburg") => {
    if (isoString) {
        const date = parseBackendDate(isoString);
        if (!isNaN(date.getTime())) {
            return new Intl.DateTimeFormat("ru-RU", {
                year: "numeric",
                month,
                day,
                timeZone, // UTC+5
            }).format(date);
        } else {
            return "";
        }
    } else {
        return "";
    }
};

export const formatTime = (isoString: string, second?: "2-digit" | "numeric", timeZone: "Asia/Vladivostok" | "Asia/Yekaterinburg" | "UTC" = "Asia/Yekaterinburg") => {
    if (isoString) {
        const date = parseBackendDate(isoString);
        if (!isNaN(date.getTime())) {
            if (second) {
                return new Intl.DateTimeFormat("ru-RU", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second,
                    timeZone,
                }).format(date);
            } else {
                return new Intl.DateTimeFormat("ru-RU", {
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone,
                }).format(date);
            }
        } else {
            return "";
        }
    } else {
        return "";
    }
};

export const formatFullTime = (isoString?: string, timeZone: "Asia/Vladivostok" | "Asia/Yekaterinburg" | "UTC" = "Asia/Yekaterinburg") => {
    if (isoString) {
        const date = parseBackendDate(isoString);
        if (!isNaN(date.getTime())) {
            return new Intl.DateTimeFormat("ru-RU", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZone,
            }).format(date);
        } else {
            return "";
        }
    } else {
        return "";
    }
};

// Курс приходит из арифметики с плавающей точкой (5.10 + 0.01 === 5.109999999999999),
// поэтому перед сохранением и печатью его нужно приводить к разумной точности.
export const roundRate = (value: number | string, digits = 4) => +(+value || 0).toFixed(digits);

export function roundMoney(value: number) {
    return Math.round(Number(value.toFixed(2)));
}

export const getRoles = (roles: Role[]): RolesData => {
    const franchise = roles.find(role => role.code.indexOf(RolesEnum.Franchise) >= 0);
    const mainCashier = roles.find(role => role.code.indexOf(RolesEnum.MainCashier) >= 0);
    const manager = roles.find(role => role.code.indexOf(RolesEnum.Manager) >= 0);

    return {
        isAdmin: roles.some(role => role.code === RolesEnum.Admin),
        isDirector: roles.some(role => role.code === RolesEnum.Director),
        isTreasurer: roles.some(role => role.code === RolesEnum.Treasurer),
        isTreasurerViewer: roles.some(role => role.code === RolesEnum.TreasurerViewer),
        isAccountant: roles.some(role => role.code === RolesEnum.Accountant),
        mainCashierCity: mainCashier ? mainCashier.code.replace(RolesEnum.MainCashier + "_", "") : null,
        isCashier: roles.some(role => role.code === RolesEnum.Cashier),
        isTablo: roles.some(role => role.code === RolesEnum.Tablo),
        isHR: roles.some(role => role.code === RolesEnum.HR),
        isNationalBank: roles.some(role => role.code === RolesEnum.NationalBank),
        isAnalyst: roles.some(role => role.code === RolesEnum.Analyst),
        isTimesheet: roles.some(role => role.code === RolesEnum.Timesheet),
        // В Camunda код роли инкассатора приходит в нижнем регистре ("collector")
        isCollector: roles.some(role => role.code?.toLowerCase() === RolesEnum.Collector.toLowerCase()),
        franchiseTenantId: franchise ? +(franchise?.code?.replace(RolesEnum.Franchise + "_", "") || 0) : 0,
        acceptTenantIds: roles
            .filter(role => role.code.indexOf(RolesEnum.AcceptTenant + "_") === 0)
            .map(role => +role.code.replace(RolesEnum.AcceptTenant + "_", ""))
            .filter(tenantId => tenantId > 0),
        managerCity: manager ? manager.code.replace(RolesEnum.Manager + "_", "") : null,
    };
};

export function transliterateName(name: string): string {
    const map: Record<string, string> = {
        А: "A", а: "a",
        Б: "B", б: "b",
        В: "V", в: "v",
        Г: "G", г: "g",
        Д: "D", д: "d",
        Е: "E", е: "e",
        Ё: "Yo", ё: "yo",
        Ж: "Zh", ж: "zh",
        З: "Z", з: "z",
        И: "I", и: "i",
        Й: "Y", й: "y",
        К: "K", к: "k",
        Л: "L", л: "l",
        М: "M", м: "m",
        Н: "N", н: "n",
        О: "O", о: "o",
        П: "P", п: "p",
        Р: "R", р: "r",
        С: "S", с: "s",
        Т: "T", т: "t",
        У: "U", у: "u",
        Ф: "F", ф: "f",
        Х: "Kh", х: "kh",
        Ц: "Ts", ц: "ts",
        Ч: "Ch", ч: "ch",
        Ш: "Sh", ш: "sh",
        Щ: "Shch", щ: "shch",
        Ъ: "", ъ: "",
        Ы: "Y", ы: "y",
        Ь: "", ь: "",
        Э: "E", э: "e",
        Ю: "Yu", ю: "yu",
        Я: "Ya", я: "ya",
        Ң: "Ng", ң: "ng",
        Ұ: "U", ұ: "u",
        Қ: "Q", қ: "q",
        Ә: "Á", ә: "á",
        Ө: "Ó", ө: "ó",
        Ү: "U", ү: "u",
        І: "I", і: "i",
        Һ: "H", һ: "h"
    };

    return name
        .split("")
        .map(char => map[char] ?? char)
        .join("");
}

/** Кириллица (рус + каз + узб) → латиница. Только для сравнения имён, не для показа */
const NAME_CYR_TO_LAT: Record<string, string> = {
    а: "a", б: "b", в: "v", г: "g", ғ: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z",
    и: "i", й: "i", к: "k", қ: "k", л: "l", м: "m", н: "n", ң: "n", о: "o", ө: "o",
    п: "p", р: "r", с: "s", т: "t", у: "u", ұ: "u", ү: "u", ў: "o", ф: "f", х: "h",
    һ: "h", ц: "ts", ч: "ch", ш: "sh", щ: "shch", ъ: "", ы: "y", ь: "", і: "i",
    э: "e", ю: "yu", я: "ya", ә: "a",
};

/**
 * Приводит имя к общему латинскому «скелету», чтобы одно и то же имя,
 * набранное кириллицей и латиницей (в том числе по-разному транслитерированное), совпадало.
 * Иванов = IVANOV, Махмут = MAKHMUT = MAHMUT, Юлия = IULIIA = YULIYA,
 * Қайрат = QAIRAT = KAYRAT, Хуршид = XURSHID, Джамшид = Чамшид = JAMSHID = DZHAMSHID, Евгений = YEVGENIY.
 * Правила применяются к обеим сторонам одинаково, поэтому важна их согласованность, а не «правильность» транслита.
 */
const nameSkeleton = (name?: string | null) => {
    const latin = [...(name || "").toLowerCase()]
        .map(c => NAME_CYR_TO_LAT[c] ?? c)
        .join("")
        .normalize("NFD")        // á → a + знак ударения, знак срежется ниже
        .replace(/[^a-z]/g, ""); // диакритика, пробелы, дефисы, апострофы (o', g')

    return latin
        .replace(/dzh/g, "j").replace(/zh/g, "j")
        .replace(/shch|sch/g, "sh")
        .replace(/ch/g, "j")     // у узбеков в документах латинская J пишется кириллицей как Ч: Чамшид = JAMSHID
        .replace(/kh|x/g, "h")
        .replace(/gh/g, "g")
        .replace(/q/g, "k")
        .replace(/w/g, "v")
        .replace(/y/g, "i")
        .replace(/ng/g, "n")
        .replace(/ie/g, "e")
        .replace(/(.)\1+/g, "$1"); // Dmitrii = Dmitriy, Zhanna = Jana
};

/** Одно и то же ли имя/фамилия без учёта регистра, алфавита (кириллица/латиница) и вариантов транслитерации */
export const isSameName = (a?: string | null, b?: string | null) => nameSkeleton(a) === nameSkeleton(b);

/** Ник instagram клиента в виде "@login" (бэк может отдать его и с собачкой, и без); пустая строка, если ника нет */
export const toInstagramLogin = (nickname?: string | null) => {
    const login = (nickname || "").trim().replace(/^@+/, "");
    return login ? "@" + login : "";
};

export function numberToPhrase(num: number)
// сумма прописью для чисел от 0 до 999 триллионов
{
    var x = num.toFixed(3);
    if (+x < 0 || +x > 9999999999999.99) return false;

    const currency = 'KZT';

    const groups: (Record<"KZT", string> | string)[][] = [];

    groups[0] = [];
    groups[1] = [];
    groups[2] = [];
    groups[3] = [];
    groups[4] = [];

    groups[9] = [];

    // рубли
    // по умолчанию
    groups[0][-1] = { 'KZT': 'тенге' };
    //исключения
    groups[0][1] = { 'KZT': 'тенге' };
    groups[0][2] = { 'KZT': 'тенге' };
    groups[0][3] = { 'KZT': 'тенге' };
    groups[0][4] = { 'KZT': 'тенге' };

    // тысячи
    // по умолчанию
    groups[1][-1] = 'тысяч';
    //исключения
    groups[1][1] = 'тысяча';
    groups[1][2] = 'тысячи';
    groups[1][3] = 'тысячи';
    groups[1][4] = 'тысячи';

    // миллионы
    // по умолчанию
    groups[2][-1] = 'миллионов';
    //исключения
    groups[2][1] = 'миллион';
    groups[2][2] = 'миллиона';
    groups[2][3] = 'миллиона';
    groups[2][4] = 'миллиона';

    // миллиарды
    // по умолчанию
    groups[3][-1] = 'миллиардов';
    //исключения
    groups[3][1] = 'миллиард';
    groups[3][2] = 'миллиарда';
    groups[3][3] = 'миллиарда';
    groups[3][4] = 'миллиарда';

    // триллионы
    // по умолчанию
    groups[4][-1] = 'триллионов';
    //исключения
    groups[4][1] = 'триллион';
    groups[4][2] = 'триллиона';
    groups[4][3] = 'триллиона';
    groups[4][4] = 'триллиона';

    // копейки
    // по умолчанию
    groups[9][-1] = { 'KZT': 'тиын' };
    //исключения
    groups[9][1] = { 'KZT': 'тиын' };
    groups[9][2] = { 'KZT': 'тиын' };
    groups[9][3] = { 'KZT': 'тиын' };
    groups[9][4] = { 'KZT': 'тиын' };

    // цифры и числа
    // либо просто строка, либо 4 строки в хэше
    var names: (Record<number, string> | string)[] = [];
    names[1] = { 0: 'один', 1: 'одна', 2: 'один', 3: 'один', 4: 'один' };
    names[2] = { 0: 'два', 1: 'две', 2: 'два', 3: 'два', 4: 'два' };
    names[3] = 'три';
    names[4] = 'четыре';
    names[5] = 'пять';
    names[6] = 'шесть';
    names[7] = 'семь';
    names[8] = 'восемь';
    names[9] = 'девять';
    names[10] = 'десять';
    names[11] = 'одиннадцать';
    names[12] = 'двенадцать';
    names[13] = 'тринадцать';
    names[14] = 'четырнадцать';
    names[15] = 'пятнадцать';
    names[16] = 'шестнадцать';
    names[17] = 'семнадцать';
    names[18] = 'восемнадцать';
    names[19] = 'девятнадцать';
    names[20] = 'двадцать';
    names[30] = 'тридцать';
    names[40] = 'сорок';
    names[50] = 'пятьдесят';
    names[60] = 'шестьдесят';
    names[70] = 'семьдесят';
    names[80] = 'восемьдесят';
    names[90] = 'девяносто';
    names[100] = 'сто';
    names[200] = 'двести';
    names[300] = 'триста';
    names[400] = 'четыреста';
    names[500] = 'пятьсот';
    names[600] = 'шестьсот';
    names[700] = 'семьсот';
    names[800] = 'восемьсот';
    names[900] = 'девятьсот';

    var r = '';
    var i, j;

    var y = Math.floor(+x);

    // если НЕ ноль рублей
    if (y > 0) {
        // выделим тройки с руб., тыс., миллионами, миллиардами и триллионами
        var t = [];

        for (i = 0; i <= 4; i++) {
            t[i] = y % 1000;
            y = Math.floor(y / 1000);
        }

        var d: number[][] = [];

        // выделим в каждой тройке сотни, десятки и единицы
        for (i = 0; i <= 4; i++) {
            d[i] = [];
            d[i][0] = t[i] % 10; // единицы
            d[i][10] = t[i] % 100 - d[i][0]; // десятки
            d[i][100] = t[i] - d[i][10] - d[i][0]; // сотни
            d[i][11] = t[i] % 100; // две правых цифры в виде числа
        }

        for (i = 4; i >= 0; i--) {
            if (t[i] > 0) {
                if (names[d[i][100]])
                    r += ' ' + ((typeof (names[d[i][100]]) == 'object') ? (names[d[i][100]][i]) : (names[d[i][100]]));

                if (names[d[i][11]])
                    r += ' ' + ((typeof (names[d[i][11]]) == 'object') ? (names[d[i][11]][i]) : (names[d[i][11]]));
                else {
                    if (names[d[i][10]]) r += ' ' + ((typeof (names[d[i][10]]) == 'object') ? (names[d[i][10]][i]) : (names[d[i][10]]));
                    if (names[d[i][0]]) r += ' ' + ((typeof (names[d[i][0]]) == 'object') ? (names[d[i][0]][i]) : (names[d[i][0]]));
                }

                if (names[d[i][11]])  // если существует числительное
                    j = d[i][11];
                else
                    j = d[i][0];

                if (groups[i][j]) {
                    if (i == 0)
                        r += ' ' + (groups[i][j] as Record<"KZT", string>)[currency];
                    else
                        r += ' ' + groups[i][j];
                }
                else {
                    if (i == 0)
                        r += ' ' + (groups[i][-1] as Record<"KZT", string>)[currency];
                    else
                        r += ' ' + groups[i][-1];
                }
            }
        }

        if (t[0] == 0)
            r += ' ' + (groups[0][-1] as Record<"KZT", string>)[currency];
    }
    else
        r = 'Ноль ' + (groups[0][-1] as Record<"KZT", string>)[currency];

    y = +((+x - Math.floor(+x)) * 100).toFixed();
    let z = "";
    if (y < 10) {
        z = '0' + y;
    } else {
        z = String(y);
    }

    r = r.trim();
    r = r.substr(0, 1).toUpperCase() + r.substr(1);
    r += ' ' + z;

    y = y * 1;

    if (names[y])  // если существует числительное
        j = y;
    else
        j = y % 10;

    if (groups[9][j])
        r += ' ' + (groups[9][j] as Record<"KZT", string>)[currency];
    else
        r += ' ' + (groups[9][-1] as Record<"KZT", string>)[currency];

    return r;
}

export function isYearsBetweenDates(date1: any, date2: any, more: boolean, years = 10) {
    const release = new Date(date1?.$d ? date1?.$d : date1);
    const minEndDate = new Date(release);
    minEndDate.setFullYear(release.getFullYear() + years); // Добавляем 10 лет
    minEndDate.setDate(minEndDate.getDate() + (more ? -1 : 1)); // Вычитаем 1 день

    return new Date(date2?.$d ? date2.$d : date2) >= minEndDate;
}